// hooks/useStudentLogin.js
import { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { validationService } from "../utils/validation/userLoginValidation";
import { useStudentAuth } from "../context/StudentAuthContext";
import { useSnackbar } from "notistack";

const useStudentLogin = () => {
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    emailOrMobile: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { studentLogin, isAuthenticated } = useStudentAuth();

  // Check if already authenticated and redirect
  useEffect(() => {
    // console.log("🔍 Login hook: isAuthenticated =", isAuthenticated);
    if (isAuthenticated) {
      // console.log("🔄 Login hook: Already authenticated, redirecting to dashboard");
      navigate('/students-dashboard');
    }
  }, [isAuthenticated, navigate]);

  const validateLogin = useCallback(() => {
    const inputErrors = validationService.validateUserLogin(formData);
    setErrors(inputErrors);
    return Object.keys(inputErrors).length === 0;
  }, [formData]);

  const onChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  }, [errors]);

  const handleClickShowPassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const handleSubmit = useCallback(async () => {
    const isValid = validateLogin();
    if (!isValid) {
      enqueueSnackbar("Please fill in all required fields", { variant: "error" });
      return;
    }

    try {
      setIsLoading(true);
      // console.log("🚀 Submitting login form...");
      
      await studentLogin({
        emailOrMobile: formData.emailOrMobile,
        password: formData.password
      });
      
      // The useEffect above will handle the redirect when isAuthenticated becomes true
      enqueueSnackbar("Login successful", { variant: "success" });
      
    } catch (error) {
      console.error("❌ Login submit error:", error);
      if (error.message?.includes("rate limit")) {
        enqueueSnackbar("Too many attempts, please try again later", {
          variant: "error",
          autoHideDuration: 5000
        });
      } else {
        enqueueSnackbar(error.message || "Invalid credentials", {
          variant: "error"
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [validateLogin, formData, enqueueSnackbar, studentLogin]);

  return {
    formData,
    errors,
    showPassword,
    validateLogin,
    handleSubmit,
    onChange,
    handleClickShowPassword,
    isLoading
  };
};

export { useStudentLogin };

